import React, { useState, useEffect } from 'react';
import {
	BarChart,
	Bar,
	Cell,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
} from 'recharts';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Popover from '@material-ui/core/Popover';
import Select from '@material-ui/core/Select';
import IconButton from '@material-ui/core/IconButton';
import TuneOutlinedIcon from '@material-ui/icons/TuneOutlined';
import CircularProgress from '@material-ui/core/CircularProgress';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment';
import {
	KeyboardDatePicker,
	MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import api from '../../../config/api';

const dataset = [
	{ name: 'Contacts', Engaged: 400, Prospect: 300 },
	{ name: 'Companies', Engaged: 100, Prospect: 500 },
];

const datasetWeekly = [
	{ name: 'Contacts', Engaged: 10, Prospect: 50 },
	{ name: 'Companies', Engaged: 5, Prospect: 30 },
];

const COLORS = [
	'#7E909A',
	'#274C77',
	'#6096BA',
	'#71A5DE',
	'#A3CEF1',
	'#CAE3F7',
];

const useStyles = makeStyles((theme) => ({
	paper: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		height: '100%',

		padding: 16,
		backgroundColor: theme.palette.background.paper,
		boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
		borderRadius: 4,
	},
	filterMenu: {
		padding: 16,
	},
}));

export default function Clients() {
	const classes = useStyles();
	const theme = useTheme();

	const [data, setData] = useState(null);
	const [period, setPeriod] = useState({
		startDate: null,
		endDate: null,
	});
	const [loading, setLoading] = useState(false);
	const [frequency, setFrequency] = useState('yearly');
	const [anchorEl, setAnchorEl] = useState(null);

	const getData = () => {
		if (!loading) {
			setLoading(true);
			setData(null);
		}

		let endpoint = 'widgets/clients';
		if (frequency === 'custom') {
			endpoint = `widgets/clients/?freq=custom&startDate=${moment(
				period.startDate
			).format('MM/DD/YYYY')}&endDate=${moment(period.endDate).format(
				'MM/DD/YYYY'
			)}`;
		} else {
			endpoint = `widgets/clients/?freq=${frequency}`;
		}
		api
			.get(endpoint)
			.then((response) => {
				if (response.data.ok) {
					setData(response.data.data);
				}
				setLoading(false);
			})
			.catch(function (error) {
				console.log(error);
				setLoading(false);
			});
	};

	useEffect(() => {
		if (
			frequency === 'custom' &&
			(period.startDate === null || period.endDate === null)
		) {
			return;
		} else {
			getData();
		}
	}, [frequency, period]);

	return (
		<div className={classes.paper}>
			<div style={{ display: 'flex', alignItems: 'center' }}>
				<Typography variant='h4' color='textPrimary'>
					Clients
				</Typography>
				<IconButton
					style={{ marginLeft: 'auto', padding: 0 }}
					onClick={(e) => setAnchorEl(e.currentTarget)}
				>
					<TuneOutlinedIcon />
				</IconButton>
				<Popover
					anchorEl={anchorEl}
					onClose={() => setAnchorEl(null)}
					open={Boolean(anchorEl)}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'left',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'left',
					}}
				>
					<div className={classes.filterMenu}>
						<div>
							<Typography variant='body2' color='textSecondary'>
								Frequency
							</Typography>
							<Select
								native
								name='frequency'
								value={frequency}
								onChange={(e) => setFrequency(e.target.value)}
								variant='filled'
								fullWidth
								margin='dense'
								style={{ marginTop: 8, marginBottom: 4 }}
							>
								<option key={0} value='yearly'>
									Yearly
								</option>
								<option key={1} value='monthly'>
									Monthly
								</option>
								<option key={2} value='weekly'>
									Weekly
								</option>
								<option key={3} value='daily'>
									Today
								</option>
								<option key={4} value='custom'>
									Custom Period
								</option>
							</Select>
						</div>
						{frequency === 'custom' && (
							<MuiPickersUtilsProvider utils={DateFnsUtils}>
								<div style={{ display: 'flex', alignItems: 'center' }}>
									<KeyboardDatePicker
										name='startDate'
										disableToolbar
										placeholder='DD/MM/YYYY'
										inputVariant='filled'
										format='dd/MM/yyyy'
										openTo='year'
										views={['year', 'month', 'date']}
										margin='dense'
										fullWidth
										value={period.startDate ? period.startDate : null}
										autoOk
										variant='inline'
										invalidDateMessage='Invalid Date Format'
										onChange={(date) =>
											setPeriod((prev) => ({ ...prev, startDate: date }))
										}
										KeyboardButtonProps={{
											'aria-label': 'change date',
										}}
									/>
									<span style={{ margin: 8 }}> - </span>
									<KeyboardDatePicker
										name='endDate'
										disableToolbar
										placeholder='DD/MM/YYYY'
										inputVariant='filled'
										format='dd/MM/yyyy'
										openTo='year'
										views={['year', 'month', 'date']}
										margin='dense'
										fullWidth
										value={period.endDate ? period.endDate : null}
										autoOk
										variant='inline'
										invalidDateMessage='Invalid Date Format'
										onChange={(date) =>
											setPeriod((prev) => ({ ...prev, endDate: date }))
										}
										KeyboardButtonProps={{
											'aria-label': 'change date',
										}}
									/>
								</div>
							</MuiPickersUtilsProvider>
						)}
					</div>
				</Popover>
			</div>
			{!data ? (
				<div style={{ display: 'flex', width: '100%', height: '100%' }}>
					{loading ? (
						<CircularProgress style={{ margin: 'auto' }} />
					) : (
						<Typography
							style={{ margin: 'auto' }}
							variant='body1'
							color='textPrimary'
						>
							No Data Available
						</Typography>
					)}
				</div>
			) : (
				<ResponsiveContainer>
					<BarChart
						width={500}
						height={500}
						data={data}
						margin={{
							top: 32,
							right: 32,

							left: 32,
						}}
					>
						<XAxis dataKey='type' />
						<YAxis />
						<Tooltip />
						<Legend
							formatter={(value, entry, index) => (
								<span style={{ color: '#000000' }}>{value}</span>
							)}
							iconType='circle'
							iconSize={10}
						/>
						<Bar
							dataKey='Engaged'
							stackId='a'
							maxBarSize={56}
							fill={COLORS[2]}
						/>
						<Bar
							dataKey='Prospect'
							stackId='a'
							maxBarSize={56}
							fill={COLORS[4]}
							label={{ position: 'top' }}
						/>
					</BarChart>
				</ResponsiveContainer>
			)}
		</div>
	);
}
